import * as Sentry from '@sentry/react'

const isSentryEnabled = import.meta.env.VITE_ENABLE_SENTRY === 'true'

if (isSentryEnabled) {
  Sentry.init({
    dsn: 'https://8136298df34ec2f70ec0273c9589a9af@o4511744115146752.ingest.de.sentry.io/4511744119734352',
    integrations: [
      // send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    // Enable logs to be sent to Sentry
    enableLogs: true,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
  })
}

function getLogger(): typeof Sentry.logger {
  if (isSentryEnabled) {
    return Sentry.logger
  }

  // Sentry drops `logger.*` and `capture*` calls when it is not initialized, so fall back
  // to the console to keep them visible in builds without Sentry
  /* eslint-disable no-console */
  return {
    trace: (message, attributes) => console.debug(message, attributes ?? ''),
    debug: (message, attributes) => console.debug(message, attributes ?? ''),
    info: (message, attributes) => console.info(message, attributes ?? ''),
    warn: (message, attributes) => console.warn(message, attributes ?? ''),
    error: (message, attributes) => console.error(message, attributes ?? ''),
    fatal: (message, attributes) => console.error('[FATAL]', message, attributes ?? ''),
    fmt: Sentry.logger.fmt,
  }
  /* eslint-enable no-console */
}

export const logger = getLogger()

function getConsoleMethodForLevel(level: Sentry.SeverityLevel): 'info' | 'warn' | 'error' {
  switch (level) {
    case 'fatal':
    case 'error':
      return 'error'
    case 'warning':
      return 'warn'
    default:
      return 'info'
  }
}

function getConsoleLogForMessage(
  message: string,
  context?: Parameters<typeof Sentry.captureMessage>[1],
): { method: 'info' | 'warn' | 'error'; params: unknown[] } {
  // A severity string is the only context that changes the console method or prefix
  if (typeof context === 'string') {
    return {
      method: getConsoleMethodForLevel(context),
      params: [context === 'fatal' ? `[FATAL] ${message}` : message],
    }
  }

  // Any other context is a scope object/callback; log it alongside the message when present
  const params = context && typeof context === 'object' ? [message, context] : [message]
  return { method: 'info', params }
}

export function captureException(exception: unknown, hint?: Parameters<typeof Sentry.captureException>[1]): void {
  if (isSentryEnabled) {
    Sentry.captureException(exception, hint)
  } else {
    // eslint-disable-next-line no-console
    console.error(exception)
  }
}

export function captureReactException(
  error: unknown,
  info: Parameters<typeof Sentry.captureReactException>[1],
  hint?: Parameters<typeof Sentry.captureReactException>[2],
): void {
  if (isSentryEnabled) {
    Sentry.captureReactException(error, info, hint)
  } else {
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

export function captureMessage(message: string, context?: Parameters<typeof Sentry.captureMessage>[1]): void {
  if (isSentryEnabled) {
    Sentry.captureMessage(message, context)
  } else {
    const { method, params } = getConsoleLogForMessage(message, context)

    // eslint-disable-next-line no-console
    console[method](...params)
  }
}

export const setSentryUser = (id: string | number) => Sentry.setUser({ id: String(id) })

export const clearSentryUser = () => Sentry.setUser(null)
